import { useAppDispatch } from 'app/hooks';
import { FETCH_ALL_PARAMS } from 'constants/query';
import { useForm } from 'hooks/useForm';
import { FormEvent, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAddDocumentMutation, useDeleteDocumentMutation } from 'services/documents';
import { setErrorMessage, setSuccessMessage } from 'store/toast-slice';
import { ServerErrorType } from 'types/server-responses';
import { dataURLtoFile } from 'utils/file-helpers';
import { COMPANY_FIELDS_SCHEMA, COMPANY_INITIAL_FIELDS } from '../constants/company-fields';
import { useEditCompanyMutation, useGetCompanyWithDocumentQuery } from '../services';
import { trimValues } from 'utils/object-helpers';
import { Company } from '../types/dtos';
import { useGetCompanyOwnersQuery } from 'features/company-owner/services';
import { useGetCitiesQuery } from 'features/city/service';

const useController = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [editCompany, { isLoading: isEditCompanyLoading }] = useEditCompanyMutation();
    const [addDocument, { isLoading: isAddDocumentLoading }] = useAddDocumentMutation();
    const [deleteDocument, { isLoading: isDeleteDocumentLoading }] = useDeleteDocumentMutation();
    const { data: company } = useGetCompanyWithDocumentQuery(id as string);
    const { data: companyOwners, isLoading: isCompanyOwnersLoading } = useGetCompanyOwnersQuery(FETCH_ALL_PARAMS);
    const { data: cities, isLoading: isGetCitiesLoading } = useGetCitiesQuery(FETCH_ALL_PARAMS);

    const { formData, setInitialFormData, handleChange, handleSubmit, errors, isDirty, changedValues } = useForm({
        validationSchema: COMPANY_FIELDS_SCHEMA,
        options: {
            trackChangedValues: true,
        },
    });

    useEffect(() => {
        if (company) setInitialFormData({ ...COMPANY_INITIAL_FIELDS, ...company });
    }, [company, setInitialFormData]);

    const handleSuccess = useCallback(() => {
        dispatch(setSuccessMessage({ key: 'company.COMPANY_EDITED' }));
    }, [dispatch]);

    const handleErrors = useCallback(
        (error: ServerErrorType) => {
            const code = error.data?.errorCode;
            dispatch(setErrorMessage({ key: code }));
        },
        [dispatch],
    );

    const getPayload = useCallback(
        (documentId?: string) => {
            const data = { ...changedValues };
            if (documentId) data.documentId = documentId;
            delete data.logo;
            return trimValues(data);
        },
        [changedValues],
    );

    const onSubmit = useCallback(
        (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            handleSubmit(async () => {
                let documentId: string | undefined;
                const prevDocument = company?.documentId;
                try {
                    if (changedValues?.logo) {
                        documentId = (await addDocument(dataURLtoFile(changedValues.logo)).unwrap()).documentId;
                    }

                    const payload = getPayload(documentId);
                    await editCompany({ id: id as string, payload }).unwrap();

                    if (changedValues?.logo && prevDocument) {
                        await deleteDocument(prevDocument).unwrap();
                    }
                    handleSuccess();
                    navigate(-1);
                } catch (error) {
                    if (documentId) await deleteDocument(documentId).unwrap();
                    handleErrors(error as ServerErrorType);
                }
            });
        },
        [
            addDocument,
            changedValues,
            company?.documentId,
            deleteDocument,
            editCompany,
            getPayload,
            handleErrors,
            handleSubmit,
            handleSuccess,
            id,
            navigate,
        ],
    );

    const onCancel = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    return {
        formData: formData as Company,
        companyOwners,
        cities,
        onChange: handleChange,
        onSubmit,
        onCancel,
        dataChanged: isDirty,
        isLoading: formData === undefined || isCompanyOwnersLoading || isGetCitiesLoading,
        isEditCompanyLoading: isEditCompanyLoading || isAddDocumentLoading || isDeleteDocumentLoading,
        errors,
    };
};

export default useController;
